import { createAsyncThunk } from '@reduxjs/toolkit';
import {
	fetchProjectConfigurations,
	createProjectConfiguration,
	updateProjectConfiguration,
	fetchProjectConfiguration,
	addProjectConfigurationTemplate,
	removeProjectConfigurationTemplate,
	fetchProjectSubTypes,
	updateProjectConfigurationFolder,
} from '../../services/project-configuration.service';
import {
	setConfigurations,
	addConfiguration,
	updateConfiguration,
	setLoading,
	setError,
	setConfiguration,
	setProjectSubTypes,
	updateFolderInConfiguration,
} from '../stores/projectConfigurationStore';
import {
	ProjectConfigurationFilter,
	CreateProjectConfigurationInput,
	UpdateProjectConfigurationInput,
} from '../models/projectConfiguration';
import { showAlert } from '../stores/alertStore';

export const fetchConfigurationsThunk =
	(filter: ProjectConfigurationFilter) => async (dispatch: any) => {
		try {
			dispatch(setLoading(true));
			const res = await fetchProjectConfigurations(filter);
			dispatch(setConfigurations(res.list));
			dispatch(setLoading(false));
		} catch (err: any) {
			dispatch(setError(err.message || 'Failed to fetch configurations'));
			dispatch(setLoading(false));
		}
	};

export const fetchConfigurationThunk =
	(id: string) => async (dispatch: any) => {
		try {
			dispatch(setLoading(true));
			const res = await fetchProjectConfiguration(id);
			dispatch(setConfiguration(res));
			dispatch(setLoading(false));
		} catch (err: any) {
			dispatch(setError(err.message || 'Failed to fetch configuration'));
			dispatch(setLoading(false));
		}
	};

export const createConfigurationThunk =
	(data: CreateProjectConfigurationInput) => async (dispatch: any) => {
		try {
			dispatch(setLoading(true));
			const res = await createProjectConfiguration(data);
			dispatch(addConfiguration(res));
			dispatch(setLoading(false));
		} catch (err: any) {
			dispatch(setError(err.message || 'Failed to create configuration'));
			dispatch(setLoading(false));
		}
	};

export const updateConfigurationThunk =
	(data: UpdateProjectConfigurationInput) => async (dispatch: any) => {
		try {
			dispatch(setLoading(true));
			const res = await updateProjectConfiguration(data);
			dispatch(setConfiguration(res));
			dispatch(setLoading(false));
			dispatch(
				showAlert({
					message: 'Successfully updated',
					severity: 'success',
				})
			);
		} catch (err: any) {
			dispatch(setError(err.message || 'Failed to update configuration'));
			dispatch(setLoading(false));
			dispatch(
				showAlert({
					message: 'Failed to update configuration',
					severity: 'error',
				})
			);
		}
	};

export const addTemplateThunk =
	(configurationId: string, templateId: string) => async (dispatch: any) => {
		try {
			// Do not set loading state
			const res = await addProjectConfigurationTemplate(
				configurationId,
				templateId
			);
			dispatch(updateConfiguration(res));
			dispatch(
				showAlert({
					message: 'Successfully updated',
					severity: 'success',
				})
			);
		} catch (err: any) {
			dispatch(setError(err.message || 'Failed to add template'));
			dispatch(
				showAlert({
					message: 'Failed to add template',
					severity: 'error',
				})
			);
		}
	};

export const removeTemplateThunk =
	(configurationId: string, templateId: string) => async (dispatch: any) => {
		try {
			// Do not set loading state
			const res = await removeProjectConfigurationTemplate(
				configurationId,
				templateId
			);
			dispatch(updateConfiguration(res));
			dispatch(
				showAlert({
					message: 'Successfully updated',
					severity: 'success',
				})
			);
		} catch (err: any) {
			dispatch(setError(err.message || 'Failed to remove template'));
			dispatch(
				showAlert({
					message: 'Failed to remove template',
					severity: 'error',
				})
			);
		}
	};

export const fetchProjectSubTypesThunk = () => async (dispatch: any) => {
	try {
		dispatch(setLoading(true));
		const res = await fetchProjectSubTypes();
		dispatch(setProjectSubTypes(res));
	} catch (err: any) {
		dispatch(setError(err.message || 'Failed to fetch project sub types'));
	} finally {
		dispatch(setLoading(false));
	}
};

export const updateConfigurationFolderThunk =
	(
		configurationId: string,
		data: {
			id: string;
			display: string;
			active: boolean;
			parentFolderId?: string;
			folderPath?: string[];
		}
	) =>
	async (dispatch: any) => {
		try {
			dispatch(setLoading(true));
			const res = await updateProjectConfigurationFolder(
				configurationId,
				data
			);
			dispatch(setConfiguration(res));
			dispatch(setLoading(false));
		} catch (err: any) {
			dispatch(setError(err.message || 'Failed to update folder'));
			dispatch(setLoading(false));
		}
	};

export const updateFolderPropertiesThunk =
	(
		configurationId: string,
		data: {
			id: string;
			display: string;
			active: boolean;
			parentFolderId?: string;
			folderPath?: string[];
		}
	) =>
	async (dispatch: any) => {
		try {
			// Optimistically update the UI first
			dispatch(
				updateFolderInConfiguration({
					folderId: data.id,
					updates: {
						display: data.display,
						active: data.active,
					},
				})
			);

			// Then update the server
			await updateProjectConfigurationFolder(configurationId, data);
		} catch (err: any) {
			// If the server update fails, we could revert the optimistic update
			// For now, we'll just show an error
			dispatch(setError(err.message || 'Failed to update folder'));
			throw err;
		}
	};
